import type { GameState, GameEvent, CardDef } from '../types.js';
import type { Verb } from '../shared/targeting.js';
import { getInstance, getPlayerState, getEffectivePower } from './GameState.js';
import { applyDamage } from './CombatResolver.js';

// Per-instance verb handler: applies one verb to one character target and
// returns the events produced. Called once per target resolved by selectTargets.
type VerbHandler = (
  state: GameState,
  sourceId: string,
  targetId: string,
  catalog: Map<string, CardDef>,
  intensity: number,
) => GameEvent[];

export const VERB_HANDLERS: Record<Verb, VerbHandler> = {
  damage: (state, sourceId, targetId, catalog, intensity) => {
    const target = getInstance(state, targetId);
    applyDamage(target, intensity);
    const events: GameEvent[] = [{
      type: 'combat-damage',
      attackerInstanceId: sourceId,
      defenderInstanceId: targetId,
      damage: intensity,
    }];
    // If damage drops effective power to 0 or below, KO.
    if (getEffectivePower(state, targetId, catalog) <= 0) {
      koCharacter(state, targetId, events, catalog);
    }
    return events;
  },

  boost: (state, _sourceId, targetId, _catalog, intensity) => {
    const inst = getInstance(state, targetId);
    const oldPower = inst.currentPower;
    inst.currentPower += intensity;
    return [{
      type: 'power-changed', instanceId: targetId,
      oldPower, newPower: inst.currentPower, amount: intensity,
    }];
  },

  armor: (state, _sourceId, targetId, _catalog, intensity) => {
    const inst = getInstance(state, targetId);
    const oldArmor = inst.armor;
    inst.armor += intensity;
    return [{
      type: 'armor-changed', instanceId: targetId,
      oldArmor, newArmor: inst.armor, amount: intensity,
    }];
  },

  exhaust: (state, _sourceId, targetId) => {
    const inst = getInstance(state, targetId);
    if (inst.isExhausted) return [];
    inst.isExhausted = true;
    return [{ type: 'character-exhausted', instanceId: targetId }];
  },

  'keep-exhausted': (state, _sourceId, targetId) => {
    const inst = getInstance(state, targetId);
    // Targeting filter (Targeting.ts:isEligible) restricts the candidate
    // pool to already-exhausted characters, so we only reach here on a
    // valid target. Defensive no-op guard for the `self`/source case,
    // which bypasses pool filtering. skipNextRefresh causes the activate
    // phase to leave the character exhausted on its owner's next turn.
    // The target is ALREADY exhausted, so the observable effect is the
    // lock (skipNextRefresh), not the exhaust — emit character-locked so
    // downstream surfaces (log, future animations) can describe what
    // actually happened.
    if (!inst.isExhausted) return [];
    inst.skipNextRefresh = true;
    return [{ type: 'character-locked', instanceId: targetId }];
  },

  ready: (state, _sourceId, targetId) => {
    const inst = getInstance(state, targetId);
    if (!inst.isFresh && !inst.isExhausted) return [];
    // Locked characters (skipNextRefresh + exhausted) are not "merely
    // exhausted" — they're held in stasis through their next refresh
    // and ready should leave them locked. Targeting filters this out at
    // selection time for pool-based targets; this guard catches the
    // `self` and source-fallback paths that bypass the filter.
    if (inst.isExhausted && inst.skipNextRefresh) return [];
    inst.isFresh = false;
    inst.isExhausted = false;
    return [{ type: 'character-refreshed', instanceId: targetId }];
  },

  return: (state, _sourceId, targetId, catalog) => {
    const events: GameEvent[] = [];
    returnToHand(state, targetId, catalog, events);
    return events;
  },

  destroy: (state, _sourceId, targetId, catalog) => {
    const events: GameEvent[] = [];
    koCharacter(state, targetId, events, catalog);
    return events;
  },

  slay: (state, _sourceId, targetId, catalog, intensity) => {
    const power = getEffectivePower(state, targetId, catalog);
    if (power < intensity) return [];
    const events: GameEvent[] = [];
    koCharacter(state, targetId, events, catalog);
    return events;
  },

  silence: (state, _sourceId, targetId, catalog) => {
    const inst = getInstance(state, targetId);
    // Skip targets with nothing meaningful to silence. The event +
    // flag flip should reflect ACTUAL gameplay impact so:
    //   - downstream visual feedback (current and future) only fires
    //     when there's something the player can perceive being shut
    //     off, and
    //   - the ability icon on the card only disappears when removing
    //     it conveys real information (no more passive aura, can no
    //     longer activate, etc.).
    // Cases that match "nothing meaningful":
    //   - already silenced (idempotent — also dedupes overlapping
    //     all-enemies + single-target silences in one dispatch),
    //   - vanilla character with no ability at all,
    //   - on-play ability that's already fired (every ring character
    //     with an on-play has fired it; on-play can't be silenced
    //     pre-trigger because the trigger runs synchronously inside
    //     PLAY_CHARACTER before any reaction window).
    if (inst.abilityDisabled) return [];
    const def = catalog.get(inst.cardId);
    if (!def || def.type !== 'character') return [];
    if (!def.ability) return [];
    if (def.ability.type === 'on-play') return [];
    inst.abilityDisabled = true;
    // Power-changed flyaways are emitted naturally by the diff system
    // in main.ts: it snapshots ringStats before dispatch and computes
    // the per-instance effective-power delta after. Silencing a
    // boost-allies provider will show up as a power drop on each
    // boosted ally; silencing an intimidate provider as a power
    // gain on each enemy formerly intimidated. The caller's
    // sweepPassiveKOs (run at every dispatch site) catches any ally
    // whose lost boost drops their effective power to 0 or below.
    return [{ type: 'ability-silenced', instanceId: targetId }];
  },
};

// KO a character: remove from ring, discard its supports, send to discard.
// Exported because a few legacy compound handlers (sacrifice-heal, steal-power,
// return-exchange) also need this primitive.
//
// `catalog` is required so the rebirth-passive check can read the dying
// character's ability slot. Calling without a catalog (defensively
// optional for legacy use) skips the rebirth check entirely and falls
// through to the normal KO path.
export function koCharacter(
  state: GameState,
  instanceId: string,
  events: GameEvent[],
  catalog?: Map<string, CardDef>,
): void {
  const inst = getInstance(state, instanceId);
  const ps = getPlayerState(state, inst.owner);

  // Rebirth: instead of going to discard, restore in-place with new
  // power AND mark the ability as spent. The ability fires once per
  // instance — `abilityDisabled` guards against the trivial loop
  // where rebirth keeps repeatedly resurrecting the same character.
  // Supports detach (they don't survive the death). The character's
  // exhaustion / fresh state resets too, so a reborn character can
  // act on its owner's next turn just like a freshly-played one.
  if (!inst.abilityDisabled && catalog) {
    const def = catalog.get(inst.cardId);
    if (def?.type === 'character'
        && def.ability?.type === 'passive'
        && def.ability.effect === 'passive-rebirth') {
      const newPower = def.ability.intensity ?? 0;
      if (newPower > 0) {
        // Detach supports — they get discarded just like a normal KO,
        // so the player can't stack a heavy support load and ride
        // the rebirth to keep it.
        for (const supportId of inst.supports) {
          const supportInst = state.cardInstances.get(supportId);
          if (supportInst) {
            supportInst.zone = 'discard';
            ps.discardPile.push(supportId);
          }
        }
        inst.supports = [];
        // Strip out of any in-flight attack so the engine doesn't keep
        // counting a "dead-then-reborn" character as committed to the
        // attack — matches the cleanup the normal-KO path does.
        if (ps.attackLine.includes(instanceId)) {
          ps.attackLine = ps.attackLine.filter(id => id !== instanceId);
        }
        if (state.combatState) {
          for (const group of state.combatState.groups) {
            group.attackerIds = group.attackerIds.filter(id => id !== instanceId);
          }
          const survivingGroups: typeof state.combatState.groups = [];
          const indexRemap = new Map<number, number>();
          for (let i = 0; i < state.combatState.groups.length; i++) {
            const g = state.combatState.groups[i];
            if (g.attackerIds.length === 0) continue;
            indexRemap.set(i, survivingGroups.length);
            survivingGroups.push(g);
          }
          if (survivingGroups.length !== state.combatState.groups.length) {
            const newAssignments = new Map<number, string>();
            for (const [oldIdx, blockerId] of state.combatState.blockerAssignments) {
              const newIdx = indexRemap.get(oldIdx);
              if (newIdx !== undefined) newAssignments.set(newIdx, blockerId);
            }
            state.combatState.groups = survivingGroups;
            state.combatState.blockerAssignments = newAssignments;
          }
        }
        // Reset stats AND zone. If the character died mid-attack, its
        // zone was 'attackLine' — the normal end-of-turn loop that
        // resets that to 'ring' iterates `ps.attackLine`, and we
        // already pulled the instance out of that list above. Without
        // an explicit reset here the zone would stay 'attackLine'
        // indefinitely, and downstream zone checks (canAssignBlocker
        // for the opponent's next attack, support targeting, etc.)
        // would treat the reborn character as "not really in the
        // ring" and refuse to interact with it.
        //
        // isFresh is preserved — if the character was just played and
        // sacrificed this turn, it should still be fresh (can't attack
        // this turn). If it had already been on the board long enough
        // for fresh to wear off, it stays that way. Rebirth doesn't
        // change "how long has this character been around" in either
        // direction.
        inst.zone = 'ring';
        inst.currentPower = newPower;
        inst.armor = 0;
        inst.isExhausted = false;
        inst.abilityDisabled = true;
        // Banner the rebirth so the player sees what happened. Emitted
        // as a regular ability-triggered event so bannerAbilityTriggers
        // picks it up without a special-case event variant.
        events.push({
          type: 'ability-triggered',
          instanceId,
          abilityType: 'passive',
          effect: 'passive-rebirth',
        });
        // No `character-ko` — the character didn't actually die, so
        // downstream consumers (voice routing, cascade-KO sweep)
        // shouldn't treat it as a death.
        return;
      }
    }
  }

  for (const slot of ps.ring) {
    if (slot.instanceId === instanceId) {
      slot.instanceId = null;
      break;
    }
  }

  for (const supportId of inst.supports) {
    const supportInst = state.cardInstances.get(supportId);
    if (supportInst) {
      supportInst.zone = 'discard';
      ps.discardPile.push(supportId);
    }
  }
  inst.supports = [];

  // Pull the dying character out of any in-flight attack state so the
  // engine doesn't keep referencing a discarded instance. Mirrors what
  // the UI's defensive filter does — fixing it here too keeps the engine
  // state consistent for downstream consumers (combat resolver, AI, log).
  if (ps.attackLine.includes(instanceId)) {
    ps.attackLine = ps.attackLine.filter(id => id !== instanceId);
  }
  if (state.combatState) {
    for (const group of state.combatState.groups) {
      group.attackerIds = group.attackerIds.filter(id => id !== instanceId);
    }
    // Drop any group that lost all its attackers; rewrite the
    // blockerAssignments map to match the new group indices.
    const survivingGroups: typeof state.combatState.groups = [];
    const indexRemap = new Map<number, number>();
    for (let i = 0; i < state.combatState.groups.length; i++) {
      const g = state.combatState.groups[i];
      if (g.attackerIds.length === 0) continue;
      indexRemap.set(i, survivingGroups.length);
      survivingGroups.push(g);
    }
    if (survivingGroups.length !== state.combatState.groups.length) {
      const newAssignments = new Map<number, string>();
      for (const [oldIdx, blockerId] of state.combatState.blockerAssignments) {
        const newIdx = indexRemap.get(oldIdx);
        if (newIdx !== undefined) newAssignments.set(newIdx, blockerId);
      }
      state.combatState.groups = survivingGroups;
      state.combatState.blockerAssignments = newAssignments;
    }
  }

  inst.zone = 'discard';
  ps.discardPile.push(instanceId);
  events.push({ type: 'character-ko', instanceId, player: inst.owner });
}

// Return a character to its owner's hand, reset stats, discard supports.
export function returnToHand(
  state: GameState,
  instanceId: string,
  catalog: Map<string, CardDef>,
  events: GameEvent[],
): void {
  const inst = getInstance(state, instanceId);
  const def = catalog.get(inst.cardId);
  const ps = getPlayerState(state, inst.owner);

  for (const slot of ps.ring) {
    if (slot.instanceId === instanceId) {
      slot.instanceId = null;
      break;
    }
  }

  for (const supportId of inst.supports) {
    const supportInst = state.cardInstances.get(supportId);
    if (supportInst) {
      supportInst.zone = 'discard';
      ps.discardPile.push(supportId);
    }
  }
  inst.supports = [];

  inst.zone = 'hand';
  inst.currentPower = def && def.type === 'character' ? def.power : 0;
  inst.armor = 0;
  inst.isFresh = false;
  inst.isExhausted = false;
  ps.hand.push(instanceId);

  events.push({ type: 'card-returned', instanceId, player: inst.owner });
}
